import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, Building, MapPin, Clock, User, Award, TrendingUp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import CommentsSection from '../components/admin/CommentsSection';

export default function LeadDetail() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [lead, setLead] = useState(location.state?.lead || null);
  const [loading, setLoading] = useState(!location.state?.lead);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    // Get current user info from localStorage
    const userInfo = localStorage.getItem('leadboard_user_info');
    if (userInfo) {
      try {
        setCurrentUser(JSON.parse(userInfo));
      } catch (err) {
        console.error('Error parsing user info:', err);
      }
    }
  }, []);

  useEffect(() => {
    // If lead data was passed via navigation state, use it
    if (location.state?.lead) {
      setLead(location.state.lead);
      setLoading(false);
    } else {
      // Fallback: fetch from API if accessed directly via URL
      fetchLeadDetails();
    }
  }, [leadId]);

  const fetchLeadDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await fetch(`${REACT_APP_BACKEND_URL}/api/v1/admin/leads/${leadId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('You don\'t have permission to view this lead');
        } else if (response.status === 404) {
          throw new Error('Lead not found');
        } else {
          throw new Error('Failed to fetch lead details');
        }
      }

      const data = await response.json();
      if (data.success && data.lead) {
        setLead(data.lead);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching lead:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getGradeBadgeColor = (grade) => {
    const colors = {
      'A': 'bg-green-600',
      'B': 'bg-blue-600',
      'C': 'bg-yellow-600',
      'D': 'bg-orange-600',
      'F': 'bg-red-600'
    };
    return colors[grade] || 'bg-gray-600';
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      'new': 'bg-blue-500 text-white',
      'contacted': 'bg-purple-500 text-white',
      'qualified': 'bg-green-500 text-white',
      'unqualified': 'bg-red-500 text-white',
      'spam': 'bg-gray-700 text-white'
    };
    return colors[status] || 'bg-gray-500 text-white';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-ivory flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-navy mx-auto"></div>
          <p className="mt-4 text-navy font-semibold">Loading lead details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-ivory flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-navy mb-2">Error Loading Lead</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button data-testid="lead-detail-button-1" 
            onClick={() => navigate('/leadboard')}
            className="bg-navy hover:bg-navy/90 text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Leads
          </Button>
        </div>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="min-h-screen bg-ivory flex items-center justify-center">
        <p className="text-gray-500">No lead data available</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ivory">
      {/* Top Navigation Bar */}
      <div className="bg-navy border-b-4 border-gold sticky top-0 z-10 shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button 
              onClick={() => navigate(-1)}
              variant="ghost"
              className="text-white hover:bg-white/10"
              data-testid="back-to-leads-button"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Leads
            </Button>
            <div className="text-right">
              <h1 className="text-2xl font-bold text-white">
                {lead.first_name} {lead.last_name}
              </h1>
              <div className="flex gap-2 mt-2 justify-end flex-wrap">
                {lead.grade && (
                  <Badge className={`${getGradeBadgeColor(lead.grade)} text-white font-semibold px-3 py-1`} data-testid="lead-grade-badge">
                    Grade {lead.grade}
                  </Badge>
                )}
                {lead.score !== undefined && (
                  <Badge className="bg-gold text-navy font-semibold px-3 py-1" data-testid="lead-score-badge">
                    Score: {lead.score}
                  </Badge>
                )}
                <Badge className={getStatusBadgeColor(lead.status) + " font-semibold px-3 py-1"} data-testid="lead-status-badge">
                  {lead.status}
                </Badge>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="space-y-6">
          {/* Contact Information Card */}
          <div className="bg-white rounded-lg border-2 border-navy shadow-md" data-testid="contact-information-card">
            <div className="bg-navy bg-opacity-10 px-6 py-4 border-b border-navy">
              <h2 className="font-bold text-navy text-xl flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Contact Information
              </h2>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <span className="text-xs text-gray-500 uppercase font-semibold block mb-1">Email</span>
                <div className="font-medium text-navy text-lg">{lead.email}</div>
              </div>
              {lead.phone && (
                <div>
                  <span className="text-xs text-gray-500 uppercase font-semibold block mb-1">Phone</span>
                  <div className="font-medium text-navy text-lg">{lead.phone}</div>
                </div>
              )}
              {lead.business_name && (
                <div>
                  <span className="text-xs text-gray-500 uppercase font-semibold block mb-1">Business Name</span>
                  <div className="font-medium text-navy text-lg">{lead.business_name}</div>
                </div>
              )}
              {lead.location && (
                <div>
                  <span className="text-xs text-gray-500 uppercase font-semibold block mb-1">Location</span>
                  <div className="font-medium text-navy text-lg">{lead.location}</div>
                </div>
              )}
              <div className="md:col-span-2">
                <span className="text-xs text-gray-500 uppercase font-semibold block mb-1">Email Verified</span>
                <div className="font-medium mt-1">
                  {lead.email_verified ? 
                    <span className="text-green-600 font-semibold text-lg">✅ Verified</span> : 
                    <span className="text-red-600 font-semibold text-lg">❌ Not Verified</span>
                  }
                </div>
              </div>
            </div>
          </div>

          {/* Lead Qualification Details - Only show if qualified AND fields exist */}
          {lead.status === 'qualified' && (lead.audience || lead.industry || lead.annual_revenue || lead.entity_type || lead.states || lead.services_interested || lead.values_fit) && (
            <div className="bg-white rounded-lg border-2 border-teal shadow-md" data-testid="qualification-details-card">
              <div className="bg-teal bg-opacity-10 px-6 py-4 border-b border-teal">
                <h2 className="font-bold text-teal text-xl flex items-center gap-2">
                  <Award className="w-5 h-5" />
                  Lead Qualification Details
                </h2>
              </div>
              <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                {lead.audience && (
                  <div>
                    <span className="text-xs text-gray-500 uppercase font-semibold block mb-1">Audience</span>
                    <div className="font-medium text-navy">{lead.audience}</div>
                  </div>
                )}
                {lead.industry && (
                  <div>
                    <span className="text-xs text-gray-500 uppercase font-semibold block mb-1">Industry</span>
                    <div className="font-medium text-navy">{lead.industry}</div>
                  </div>
                )}
                {lead.annual_revenue && (
                  <div>
                    <span className="text-xs text-gray-500 uppercase font-semibold block mb-1">Annual Revenue</span>
                    <div className="font-medium text-navy">{lead.annual_revenue}</div>
                  </div>
                )}
                {lead.entity_type && (
                  <div>
                    <span className="text-xs text-gray-500 uppercase font-semibold block mb-1">Entity Type</span>
                    <div className="font-medium text-navy">{lead.entity_type}</div>
                  </div>
                )}
                {lead.states && (
                  <div className="md:col-span-2">
                    <span className="text-xs text-gray-500 uppercase font-semibold block mb-1">States of Interest</span>
                    <div className="font-medium text-navy">{lead.states}</div>
                  </div>
                )}
                {lead.services_interested && (
                  <div className="md:col-span-2">
                    <span className="text-xs text-gray-500 uppercase font-semibold block mb-1">Services Interested</span>
                    <div className="font-medium text-navy">{lead.services_interested}</div>
                  </div>
                )}
                {lead.values_fit && (
                  <div className="md:col-span-2">
                    <span className="text-xs text-gray-500 uppercase font-semibold block mb-1">Values Fit</span>
                    <div className="font-medium text-navy">{lead.values_fit}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Their Story */}
          {lead.story && (
            <div className="bg-white rounded-lg border-2 border-gold shadow-md" data-testid="story-card">
              <div className="bg-gold bg-opacity-10 px-6 py-4 border-b border-gold">
                <h2 className="font-bold text-gold text-xl">Their Story</h2>
              </div>
              <div className="p-6">
                <p className="text-navy italic leading-relaxed text-lg">{lead.story}</p>
              </div>
            </div>
          )}

          {/* Scoring & Routing - Only visible to admin */}
          {(lead.grade || lead.score !== undefined || lead.breakdown || lead.routing_info) && (
            <div className="bg-white rounded-lg border-2 border-navy shadow-md" data-testid="scoring-routing-card">
              <div className="bg-navy px-6 py-4">
                <h2 className="font-bold text-white text-xl flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Scoring & Routing
                </h2>
              </div>
              <div className="p-6 space-y-6">
                {/* Score Summary */}
                {(lead.grade || lead.score !== undefined) && (
                  <div className="flex gap-4">
                    {lead.grade && (
                      <div className="flex-1 bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <span className="text-xs text-gray-500 uppercase font-semibold block mb-2">Grade</span>
                        <div className={`text-3xl font-bold ${getGradeBadgeColor(lead.grade)} bg-clip-text text-transparent`}>
                          {lead.grade}
                        </div>
                      </div>
                    )}
                    {lead.score !== undefined && (
                      <div className="flex-1 bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <span className="text-xs text-gray-500 uppercase font-semibold block mb-2">Total Score</span>
                        <div className="text-3xl font-bold text-gold">{lead.score}/100</div>
                      </div>
                    )}
                  </div>
                )}

                {/* Score Breakdown */}
                {lead.breakdown && (
                  <div className="space-y-3">
                    <h3 className="font-semibold text-navy">Score Breakdown</h3>
                    {lead.breakdown.budget !== undefined && (
                      <div>
                        <div className="flex justify-between mb-1">
                          <span className="text-sm text-gray-600">Budget (40 points)</span>
                          <span className="text-sm font-semibold">{lead.breakdown.budget}/40</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-green-600 h-2 rounded-full transition-all" 
                            style={{width: `${(lead.breakdown.budget / 40) * 100}%`}}
                          ></div>
                        </div>
                      </div>
                    )}
                    {lead.breakdown.need !== undefined && (
                      <div>
                        <div className="flex justify-between mb-1">
                          <span className="text-sm text-gray-600">Need (30 points)</span>
                          <span className="text-sm font-semibold">{lead.breakdown.need}/30</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full transition-all" 
                            style={{width: `${(lead.breakdown.need / 30) * 100}%`}}
                          ></div>
                        </div>
                      </div>
                    )}
                    {lead.breakdown.timeline !== undefined && (
                      <div>
                        <div className="flex justify-between mb-1">
                          <span className="text-sm text-gray-600">Timeline (15 points)</span>
                          <span className="text-sm font-semibold">{lead.breakdown.timeline}/15</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-purple-600 h-2 rounded-full transition-all" 
                            style={{width: `${(lead.breakdown.timeline / 15) * 100}%`}}
                          ></div>
                        </div>
                      </div>
                    )}
                    {lead.breakdown.behavioral !== undefined && (
                      <div>
                        <div className="flex justify-between mb-1">
                          <span className="text-sm text-gray-600">Behavioral (15 points)</span>
                          <span className="text-sm font-semibold">{lead.breakdown.behavioral}/15</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-yellow-600 h-2 rounded-full transition-all" 
                            style={{width: `${(lead.breakdown.behavioral / 15) * 100}%`}}
                          ></div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Routing Info */}
                {lead.routing_info && (
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h3 className="font-semibold text-navy mb-3">Routing Information</h3>
                    <div className="space-y-2">
                      {lead.routing_info.recommended_department && (
                        <div>
                          <span className="text-xs text-gray-500 uppercase font-semibold block">Recommended Department</span>
                          <span className="text-navy font-medium">{lead.routing_info.recommended_department}</span>
                        </div>
                      )}
                      {lead.routing_info.priority_tier && (
                        <div>
                          <span className="text-xs text-gray-500 uppercase font-semibold block">Priority Tier</span>
                          <span className="text-navy font-medium">{lead.routing_info.priority_tier}</span>
                        </div>
                      )}
                      {lead.routing_info.recommendation && (
                        <div>
                          <span className="text-xs text-gray-500 uppercase font-semibold block">Recommendation</span>
                          <span className="text-navy font-medium">{lead.routing_info.recommendation}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Spam Flags - Only if present */}
          {lead.spam_flags && lead.spam_flags.length > 0 && (
            <div className="bg-red-50 rounded-lg border-2 border-red-500 shadow-md" data-testid="spam-flags-card">
              <div className="bg-red-500 px-6 py-4">
                <h2 className="font-bold text-white text-xl">⚠️ Spam Flags</h2>
              </div>
              <div className="p-6">
                <div className="flex flex-wrap gap-2">
                  {(lead.spam_flags || []).map((flag, index) => (
                    <Badge key={index} className="bg-red-600 text-white font-semibold">
                      {flag}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Geolocation & Security - Only visible to admin */}
          {(lead.country || lead.city || lead.ip_address || lead.vpn || lead.tor || lead.high_risk_ip) && (
            <div className="bg-white rounded-lg border-2 border-gray-300 shadow-md" data-testid="geolocation-card">
              <div className="bg-gray-100 px-6 py-4 border-b border-gray-300">
                <h2 className="font-bold text-gray-700 text-xl flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Geolocation & Security
                </h2>
              </div>
              <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                {lead.country && (
                  <div>
                    <span className="text-xs text-gray-500 uppercase font-semibold block mb-1">Country</span>
                    <div className="font-medium text-navy">{lead.country}</div>
                  </div>
                )}
                {lead.city && (
                  <div>
                    <span className="text-xs text-gray-500 uppercase font-semibold block mb-1">City</span>
                    <div className="font-medium text-navy">{lead.city}</div>
                  </div>
                )}
                {lead.region && (
                  <div>
                    <span className="text-xs text-gray-500 uppercase font-semibold block mb-1">Region</span>
                    <div className="font-medium text-navy">{lead.region}</div>
                  </div>
                )}
                {lead.isp && (
                  <div>
                    <span className="text-xs text-gray-500 uppercase font-semibold block mb-1">ISP</span>
                    <div className="font-medium text-navy">{lead.isp}</div>
                  </div>
                )}
                {lead.timezone && (
                  <div>
                    <span className="text-xs text-gray-500 uppercase font-semibold block mb-1">Timezone</span>
                    <div className="font-medium text-navy">{lead.timezone}</div>
                  </div>
                )}
                {lead.ip_address && (
                  <div>
                    <span className="text-xs text-gray-500 uppercase font-semibold block mb-1">IP Address</span>
                    <div className="font-medium text-navy font-mono">{lead.ip_address}</div>
                  </div>
                )}
                {/* Security Flags */}
                <div className="md:col-span-2 flex gap-3 flex-wrap">
                  {lead.vpn !== undefined && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-600">VPN:</span>
                      <span className={lead.vpn ? "text-red-600 font-semibold" : "text-green-600"}>
                        {lead.vpn ? "⚠️ Yes" : "✅ No"}
                      </span>
                    </div>
                  )}
                  {lead.tor !== undefined && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-600">TOR:</span>
                      <span className={lead.tor ? "text-red-600 font-semibold" : "text-green-600"}>
                        {lead.tor ? "⚠️ Yes" : "✅ No"}
                      </span>
                    </div>
                  )}
                  {lead.high_risk_ip !== undefined && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-600">High Risk IP:</span>
                      <span className={lead.high_risk_ip ? "text-red-600 font-semibold" : "text-green-600"}>
                        {lead.high_risk_ip ? "⚠️ Yes" : "✅ No"}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Timeline */}
          {lead.created_at && (
            <div className="bg-white rounded-lg border-2 border-gray-300 shadow-md" data-testid="timeline-card">
              <div className="bg-gray-100 px-6 py-4 border-b border-gray-300">
                <h2 className="font-bold text-gray-700 text-xl flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Timeline
                </h2>
              </div>
              <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                {lead.created_at && (
                  <div>
                    <span className="text-xs text-gray-500 uppercase font-semibold block mb-1">Created</span>
                    <div className="font-medium text-navy">{new Date(lead.created_at).toLocaleString()}</div>
                  </div>
                )}
                {lead.updated_at && (
                  <div>
                    <span className="text-xs text-gray-500 uppercase font-semibold block mb-1">Last Updated</span>
                    <div className="font-medium text-navy">{new Date(lead.updated_at).toLocaleString()}</div>
                  </div>
                )}
                {lead.verified_at && (
                  <div>
                    <span className="text-xs text-gray-500 uppercase font-semibold block mb-1">Verified</span>
                    <div className="font-medium text-navy">{new Date(lead.verified_at).toLocaleString()}</div>
                  </div>
                )}
                <div className="md:col-span-3">
                  <span className="text-xs text-gray-500 uppercase font-semibold block mb-1">Current Status</span>
                  <Badge className={getStatusBadgeColor(lead.status) + " font-semibold px-3 py-1"}>
                    {lead.status}
                  </Badge>
                </div>
              </div>
            </div>
          )}

          {/* Comments Section */}
          <div className="bg-white rounded-lg border-2 border-gold shadow-md" data-testid="comments-section-card">
            <div className="bg-gold bg-opacity-10 px-6 py-4 border-b border-gold">
              <h2 className="font-bold text-gold text-xl">Comments & Notes</h2>
            </div>
            <div className="p-6">
              <CommentsSection 
                entityId={leadId} 
                entityType="lead" 
                currentUser={currentUser}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
